import * as React from 'react'
import { ReactElement } from 'react'
import { FieldArray, FormikContextType, useFormikContext } from 'formik'
import { FileMutationDto } from '../../../../config/fileConfig'
import { Stack } from '@fluentui/react'
import UserTierList from '../../UserTier/UserTierList'
import { Field } from '@fluentui/react-components'
import MoneyInput from '../../../Input/MoneyInput'
import { InsolvencyModuleDto, useFileModule } from '../../../../hooks/module/useFileModule'
import { ModuleType } from '../../../../context/GlobalContext'
import { RateFieldProps } from './RateFields'
import { InsolvencyTierDto } from '../../../../config/finance/financeConfig'
import { UserInsolvencyTierListItem } from '../../UserTier/UserInsolvencyTierListItem'
import UserInsolvencyTierFormContainer from '../../UserTier/UserInsolvencyTierFormContainer'
import { UserTierFormProps } from '../../UserTier/UserTierForm'
import { UserDto } from '../../../../config/userConfig'
import { getDefaultTierValue } from './RateFieldsContents'
import FormattableNumberInput, { CommitEventType } from '../../../Input/FormattableNumberInput'
import { useTranslation } from 'react-i18next'

function getDefaultInsolvencyTierValue(currency: string, insolvencyModule: InsolvencyModuleDto): InsolvencyTierDto {
    return {
        ...getDefaultTierValue(currency),
        type: 'INSOLVENCY_TIER',
        role: undefined,
        estateRate: insolvencyModule.rateEstate || '100.00',
        factorExperience: '1.0',
        factorEstate: insolvencyModule.factorEstate || '1.0',
        factorTotal: insolvencyModule.factorEstate || '1.0',
    }
}

export default function RateFieldsInsolvencyContents({ orientation }: RateFieldProps): ReactElement {
    const { t: translate } = useTranslation()

    const { values, setFieldValue } = useFormikContext<FileMutationDto>()
    const { fileModule: insolvencyModule, index: moduleNumber } = useFileModule<InsolvencyModuleDto>(
        ModuleType.Insolvency,
        values,
    )

    return (
        <>
            {insolvencyModule && (
                <>
                    <Field label={translate('file.edit.form.finance.rates.factorEstate')} orientation={orientation}>
                        <FormattableNumberInput
                            allowNegative={false}
                            decimals={1}
                            value={insolvencyModule?.factorEstate}
                            onCommit={(eventType: CommitEventType, value: string) => {
                                if (eventType !== CommitEventType.OnLoad) {
                                    setFieldValue(`modules[${moduleNumber}].factorEstate`, value)
                                }
                            }}
                        />
                    </Field>
                    <Field label={translate('file.edit.form.finance.rates.rateEstate')} orientation={orientation}>
                        <MoneyInput
                            value={insolvencyModule?.rateEstate}
                            currency={values.currency}
                            onCommit={(eventType: CommitEventType, value: string) => {
                                if (eventType !== CommitEventType.OnLoad) {
                                    setFieldValue(`modules[${moduleNumber}.rateEstate]`, value)
                                }
                            }}
                        />
                    </Field>
                </>
            )}
            <Stack>
                <FieldArray name="tiers">
                    {() => (
                        <UserTierList<InsolvencyTierDto>
                            defaultTierValue={() => getDefaultInsolvencyTierValue(values.currency, insolvencyModule)}
                            createListItem={(index: number, item: InsolvencyTierDto, editTier) => (
                                <UserInsolvencyTierListItem
                                    index={index}
                                    item={item}
                                    editTier={editTier}
                                    insolvencyModule={insolvencyModule}
                                />
                            )}
                            createTierFormContainer={(
                                props: UserTierFormProps<InsolvencyTierDto>,
                                fileContext: FormikContextType<FileMutationDto>,
                                selectableUsers: UserDto[],
                            ) => (
                                <UserInsolvencyTierFormContainer
                                    {...props}
                                    fileContext={fileContext}
                                    selectableUsers={selectableUsers}
                                    insolvencyModule={insolvencyModule}
                                />
                            )}
                        />
                    )}
                </FieldArray>
            </Stack>
        </>
    )
}
